import { Component, type ErrorInfo, type ReactNode } from 'react';
import { t } from '../i18n/context';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({ componentStack: errorInfo.componentStack ?? null });
    try {
      sessionStorage.setItem('condor_last_error', JSON.stringify({
        name: error?.name,
        message: error?.message,
        stack: error?.stack,
        componentStack: errorInfo.componentStack,
        at: new Date().toISOString(),
      }));
    } catch {
      // storage full or disabled; drop silently
    }
  }

  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      const showDetails = typeof window !== 'undefined'
        && (window.location.hostname === 'localhost' || window.location.hostname.startsWith('app.'));
      return (
        <div className="min-h-screen flex items-center justify-center px-4 py-8" role="alert">
          <div className="text-center max-w-2xl w-full">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--danger)]/10 border border-[var(--danger)]/20 flex items-center justify-center">
              <span className="text-2xl" aria-hidden="true">!</span>
            </div>
            <h1 className="text-2xl font-bold mb-2">{t('errors.boundary_title')}</h1>
            <p className="text-[var(--text-muted)] mb-6">
              {t('errors.boundary_fallback')}
            </p>
            {showDetails && err && (
              <details className="text-left mb-6 border border-[var(--border-subtle)] rounded-lg bg-[var(--bg-card)] p-4">
                <summary className="cursor-pointer text-sm font-medium text-[var(--danger-fg)]">
                  {err.name}: {err.message}
                </summary>
                <pre className="mt-3 text-xs overflow-x-auto text-[var(--text-muted)] whitespace-pre-wrap">
{err.stack}
{this.state.componentStack ? `\n\n--- component stack ---${this.state.componentStack}` : ''}
                </pre>
              </details>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null, componentStack: null });
                }}
                className="px-6 py-2.5 border border-[var(--border-medium)] text-[var(--text-body)] rounded-lg hover:bg-[var(--bg-hover)] transition"
              >
                {t('errors.boundary_retry')}
              </button>
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null, componentStack: null });
                  window.location.href = '/dashboard';
                }}
                className="px-6 py-2.5 bg-[var(--accent)] hover:bg-[var(--accent-strong)] text-white font-medium rounded-lg transition"
              >
                {t('errors.boundary_button')}
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
